import { useMemo } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Typography from '@mui/material/Typography';
import { createColumnHelper } from '@tanstack/react-table';

import { FilterDimension } from '../schemas';
import { EfficiencyInsight, EfficiencyKpiType } from '../types';

import { usePlatform } from '@/features/users';
import { Tooltip } from '@/shared/components/tooltip';

const columnHelper = createColumnHelper<EfficiencyInsight>();

const CPV_UNAVAILABLE_FOR_REGION =
  'CPV is not available for the Region category.';

const useEffiencyInsightsColumns = (
  efficiencyCategory: FilterDimension,
  kpiType: EfficiencyKpiType
) => {
  const platform = usePlatform();

  // CPV is not available for the Region category on Meta: keep the existing
  // tooltip behaviour and hide the values. For every other case (including
  // Meta Region with CPM) we render the actual kpi values.
  const isCpvUnavailableForRegion =
    platform === 'Meta' &&
    efficiencyCategory.toLowerCase() === 'region' &&
    kpiType.toUpperCase() === 'CPV';

  const columns = useMemo(
    () => [
      columnHelper.accessor('ranking', {
        header: () => (
          <Typography variant='body3' component='span'>
            Ranking
          </Typography>
        ),
        cell: (props) => (
          <Typography variant='subtitleNumbers' component='span'>
            {props.getValue()}
          </Typography>
        ),
        size: 80,
      }),
      columnHelper.accessor('categoryName', {
        header: () => (
          <Typography variant='body3' component='span'>
            Name
          </Typography>
        ),
        cell: (props) => (
          <Typography variant='subtitleNumbers' component='span'>
            {props.getValue()}
          </Typography>
        ),
        size: 320,
      }),
      columnHelper.accessor('kpi', {
        id: kpiType,
        header: () => (
          <Typography
            variant='body3'
            component='span'
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {kpiType.toUpperCase()}
            {isCpvUnavailableForRegion && (
              <Tooltip title={CPV_UNAVAILABLE_FOR_REGION}>
                <InfoOutlinedIcon
                  sx={{ fontSize: 14, color: 'warning.main' }}
                />
              </Tooltip>
            )}
          </Typography>
        ),
        cell: (props) => {
          const value = props.getValue();
          return (
            <Typography variant='subtitleNumbers' component='span'>
              {isCpvUnavailableForRegion ? '-' : `$${value}`}
            </Typography>
          );
        },
        size: 100,
      }),
      columnHelper.accessor('efficiencyIndex', {
        header: () => (
          <Typography variant='body3' component='span'>
            Efficiency Index
          </Typography>
        ),
        cell: (props) => (
          <Typography variant='subtitleNumbers' component='span'>
            {new Intl.NumberFormat('en-US', {
              maximumFractionDigits: 2,
            }).format(props.getValue())}
          </Typography>
        ),
        size: 140,
      }),
    ],
    [kpiType, isCpvUnavailableForRegion]
  );

  return columns;
};

export default useEffiencyInsightsColumns;
